/**
 * Product card image carousel (collection grid)
 * ------------------------------------------------
 * Lets a shopper cycle through a product's images from the collection
 * grid without leaving the page: click arrows + a hover-revealed progress
 * bar on desktop, swipe + an always-visible progress bar on touch.
 *
 * Markup contract (see snippets/product-card-carousel.liquid, rendered by
 * snippets/product-grid-item.liquid):
 *   <div class="grid-product__carousel" data-product-carousel data-active-index="0">
 *     <div class="grid-product__carousel-track" data-carousel-track>
 *       <div class="grid-product__carousel-slide" data-active>...</div>  (real slide 0)
 *       <div class="grid-product__carousel-slide">...</div>              (real slide 1, etc)
 *     </div>
 *     <button data-carousel-prev>...</button>
 *     <button data-carousel-next>...</button>
 *     <div data-carousel-progress>
 *       <span class="grid-product__carousel-progress-segment is-active"></span>
 *       <span class="grid-product__carousel-progress-segment"></span>
 *     </div>
 *   </div>
 *
 * No dependencies, no per-card setup cost: everything is delegated off a
 * handful of document-level listeners so it works identically for cards
 * rendered on initial page load and cards injected later (filters,
 * infinite scroll, quick shop re-renders, etc).
 *
 * Looping removed (2026-09-22, per Ervin -- see
 * claude/pdp-carousel-mobile-crash-diagnosis.md): from 2026-09-21 through
 * this change, reaching either end continued in the same direction into
 * the true first/last slide instead of stopping, via two extra "clone"
 * slides per carousel (a duplicate of the last real slide prepended,
 * a duplicate of the first real slide appended) so the track could glide
 * forward onto a clone and then snap back invisibly to the real slide
 * underneath it, rather than visibly rewinding backward through every
 * slide. That looping was a meaningful but, on its own, insufficient fix
 * for a mobile crash Ervin reported -- see the next entry.
 *
 * This file is now a plain finite carousel: activate() clamps the target
 * index to [0, realSlides.length - 1] instead of wrapping around clone
 * slides, and the arrows disable themselves (existing
 * .grid-product__carousel-arrow:disabled styling) at either end instead
 * of looping past it. There are no clone slides, no snap-back, and no
 * wrap-transition timer to manage -- getRealSlides()/hasClones()/
 * scheduleSnap() and friends are gone along with them.
 *
 * Nested-in-a-horizontal-shelf mobile crash fix (2026-09-22, per Ervin --
 * see claude/pdp-carousel-mobile-crash-diagnosis.md): removing the clone
 * slides above didn't stop the crash Ervin reported. Further diagnosis
 * found the crash was 100% reproducible reaching specific *widget*
 * contexts on mobile -- the homepage's featured-collection row, and PDP's
 * "Complete The Look" / recommendations rows -- and never on a plain
 * collection-grid page, even though both render this exact same carousel.
 * The difference: those widget contexts wrap their product grid in
 * `.grid-overflow-wrapper`, a horizontally-scrolling shelf
 * (`overflow-x: scroll`, flex row, confirmed via getComputedStyle) --
 * something a plain collection grid never does. Nesting this file's own
 * horizontal touch handling (a document-level non-passive touchmove +
 * preventDefault() once a gesture resolves 'horizontal', paired with
 * `touch-action: pan-y` on the track) inside an ancestor that ALSO wants
 * native control of horizontal touch gestures puts two systems in a fight
 * over the same gesture -- a known category of WebKit crash, and
 * consistent with everything Ervin reported: deterministic, same URL, no
 * redirect, "page reloads once then breaks to a browser error" exactly
 * when reaching one of these shelves.
 *
 * Fix: a carousel inside `.grid-overflow-wrapper` (see the matching CSS
 * override in layout/theme.liquid, `touch-action: auto` for the track in
 * that context) now only ever *passively* tracks a touch there -- enough
 * to tell a tap from a drag at touchend -- and never calls
 * preventDefault(), so the browser's native handling (the shelf's own
 * horizontal scroll, and the page's vertical scroll) is never impeded.
 * Swiping a product's carousel inside a shelf scrolls the shelf, same as
 * swiping anywhere else on it, rather than advancing that product's
 * photos; changing a single product's photo inside a shelf is arrow-tap
 * only. Outside a shelf (plain collection grids, where this never broke),
 * behavior is unchanged from before this fix.
 *
 * Hover-out behavior (2026-09-23, per Ervin): a card used to reset back to
 * slide 0 on mouseleave, so the next hover always started from the top.
 * Ervin asked for that removed -- a card should stay on whatever slide the
 * shopper last navigated to even after the pointer leaves it, only
 * resetting (implicitly, via a fresh page load / re-render) on its own.
 * There is intentionally no mouseleave handling here anymore.
 *
 * Diagonal-swipe page scroll lock (2026-09-22, per Ervin) -- still open,
 * unrelated to the fixes above: a swipe that's mostly horizontal but not
 * perfectly axis-aligned (i.e. almost every real touch) used to scroll the
 * page vertically at the same time as advancing (or fighting to advance)
 * the carousel, because `touch-action: pan-y` on the track (see
 * layout/theme.liquid) tells the browser it's free to start a native
 * vertical scroll the instant it sees *any* vertical component in the
 * gesture, and the old touchmove listener was `{ passive: true }` -- a
 * promise to the browser that this code would never call
 * `preventDefault()`, so the browser never even waited around to ask.
 *
 * First attempt (2026-09-22) added a direction lock that waited for 10px
 * of movement, accumulated across however many touchmove events it took,
 * before deciding 'horizontal' vs 'vertical' and (if horizontal) calling
 * preventDefault(). Ervin reported this didn't actually stop the page
 * from scrolling on a real phone. Root cause: per the CSS touch-action
 * spec, the browser resolves whether a touch sequence is native-panned or
 * script-handled *once*, very early -- effectively on the first touchmove
 * event that has a vertical component and isn't prevented. Waiting to
 * accumulate 10px meant several early touchmove events went by
 * unprevented (since we hadn't decided yet), which was enough for the
 * browser to already commit to native pan-y scrolling -- and a
 * preventDefault() called on a *later* touchmove, after that commitment,
 * is simply ignored. (This also explains why the earlier verification via
 * synthetically dispatched TouchEvents looked fine: dispatchEvent() only
 * invokes JS listeners, it never drives the browser's actual native
 * touch/compositor gesture recognizer, so that test could confirm the
 * lock logic but not this timing race against real hardware input.)
 *
 * Fixed by deciding direction on the very first touchmove event instead
 * of waiting to accumulate distance -- using whatever delta already
 * exists at that point (real touch hardware reports move events well
 * before 10px of travel, so there's always some non-zero reading). This
 * puts our preventDefault() call inside the same early window the browser
 * itself uses to resolve the gesture, rather than after it. A gesture
 * that resolves 'vertical' is left alone for the rest of it -- native
 * pan-y scrolling handles it exactly as before, with no added latency. A
 * 'horizontal' one calls event.preventDefault() on every touchmove for
 * the rest of the gesture, which requires (and only works with) a
 * non-passive listener -- so listening for touchmove non-passively is
 * still scoped to exactly the span of one carousel touch (added in
 * touchstart, removed in touchend/touchcancel) rather than left on
 * `document` permanently, so ordinary page scrolling everywhere else
 * pays zero added cost from this.
 *
 * A third attempt (v3, 2026-09-23) went further still: Ervin reported
 * this "v2" logic *still* didn't stop the diagonal scroll on a real
 * iPhone 14 (Safari and Chrome), which turned out to be a WebKit quirk --
 * `touch-action: pan-y` on the track isn't just a hint, WebKit treats it
 * as an unconditional promise that vertical panning is native and doesn't
 * reliably honor preventDefault() against it, no matter how early it's
 * called. v3 set `touch-action: none` on the track instead (removing that
 * promise entirely) and had this file manually drive vertical scrolling
 * itself via window.scrollBy() on every touchmove.
 *
 * v3 was reverted the same day: Ervin reported that manual scroll made
 * ordinary vertical scrolling on a carousel card choppy -- small discrete
 * jumps rather than a smooth scroll, since window.scrollBy() per
 * touchmove has none of the native compositor's interpolation or
 * momentum that `pan-y` provides for free. This file is back to the "v2"
 * logic below (`pan-y` + decide-on-first-touchmove), which trades a
 * smooth native vertical scroll for the diagonal-swipe bug being back and
 * still open -- see claude/product-card-carousel-touch-scroll-lock.md for
 * the full history and open options. This is a separate, still-open issue
 * from the fixes above -- none of them depend on each other.
 */
(function () {
  'use strict';

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  function getRealSlides(carousel) {
    return carousel.querySelectorAll('.grid-product__carousel-slide');
  }

  function setTrackPosition(carousel, index) {
    var track = carousel.querySelector('[data-carousel-track]');
    if (!track) return;
    track.style.transform = 'translateX(-' + index * 100 + '%)';
  }

  function activate(carousel, index) {
    var realSlides = getRealSlides(carousel);
    if (!realSlides.length) return;

    var max = realSlides.length - 1;
    var clamped = Math.max(0, Math.min(index, max));

    setTrackPosition(carousel, clamped);
    carousel.setAttribute('data-active-index', String(clamped));

    for (var i = 0; i < realSlides.length; i++) {
      if (i === clamped) {
        realSlides[i].setAttribute('data-active', '');
      } else {
        realSlides[i].removeAttribute('data-active');
      }
    }

    var prevBtn = carousel.querySelector('[data-carousel-prev]');
    var nextBtn = carousel.querySelector('[data-carousel-next]');
    if (prevBtn) prevBtn.disabled = clamped === 0;
    if (nextBtn) nextBtn.disabled = clamped === max;

    // Progress bar fills cumulatively from the left through the current
    // slide (segments 0..index), not just the single active one, so it
    // reads as an actual progress indicator -- matching
    // https://toteme.com/en-us/collections/outerwear -- rather than a
    // position marker.
    var segments = carousel.querySelectorAll('.grid-product__carousel-progress-segment');
    for (var s = 0; s < segments.length; s++) {
      segments[s].classList.toggle('is-active', s <= clamped);
    }
  }

  // --- Click arrows (desktop) -------------------------------------------

  document.addEventListener('click', function (event) {
    var btn = closest(event.target, '[data-carousel-prev], [data-carousel-next]');
    if (!btn) return;

    var carousel = closest(btn, '[data-product-carousel]');
    if (!carousel) return;

    // Stop this from bubbling into the card's own link.
    event.preventDefault();
    event.stopPropagation();

    var current = parseInt(carousel.getAttribute('data-active-index') || '0', 10);
    var next = btn.hasAttribute('data-carousel-next') ? current + 1 : current - 1;
    activate(carousel, next);
  });

  // --- Swipe (touch) ------------------------------------------------------
  //
  // The theme's stretched product-card link overlay (.grid-product__link,
  // see product-grid-item.liquid) is disabled over the carousel on touch
  // (see same-collection-grid.liquid), specifically so touches here reach
  // this handling instead of being swallowed by that overlay. That means a
  // plain tap -- which the disabled overlay would otherwise have turned
  // into a normal link navigation -- has to be sent to the product page by
  // hand here (data-product-url, rendered by product-card-carousel.liquid).

  var SWIPE_THRESHOLD = 30; // px: a deliberate horizontal swipe
  var TAP_TOLERANCE = 10; // px: barely-moved touches still count as a tap

  var touch = null;

  function onCarouselTouchMove(event) {
    if (!touch || !event.touches.length) return;

    touch.deltaX = event.touches[0].clientX - touch.startX;
    touch.deltaY = event.touches[0].clientY - touch.startY;

    if (touch.insideShelf) {
      // Passive listener (see touchstart, below): only tracking deltas
      // here for the tap-vs-drag check at touchend. Never preventDefault
      // in this branch -- see the big comment at the top of this file for
      // why (the shelf ancestor needs uncontested native control of every
      // horizontal touch gesture).
      return;
    }

    if (touch.locked === null) {
      // Decide as early as possible -- on this very first touchmove,
      // using whatever (possibly tiny) delta already exists -- rather
      // than waiting for more distance to accumulate. See the big
      // comment at the top of this file for why: the browser resolves
      // native-pan-vs-script-handled within this same first sliver of
      // movement, so deciding any later risks losing the race to
      // preventDefault() an already-committed native scroll.
      touch.locked = Math.abs(touch.deltaX) >= Math.abs(touch.deltaY) ? 'horizontal' : 'vertical';
    }

    if (touch.locked === 'horizontal') {
      // This gesture is meant for the carousel, not the page -- stop the
      // browser's native pan-y handling from also scrolling the page for
      // the rest of it. Requires this listener to be non-passive (see
      // where it's added, below).
      event.preventDefault();
    }
    // A 'vertical' gesture is left alone: native pan-y scrolling handles
    // it with no extra latency, same as before.
  }

  function endCarouselTouch() {
    touch = null;
    // capture defaults to false on both of the addEventListener calls
    // below regardless of their differing `passive` value, and `passive`
    // itself isn't part of a listener's removal identity -- so this one
    // call correctly removes whichever of the two was actually added for
    // this touch.
    document.removeEventListener('touchmove', onCarouselTouchMove);
  }

  document.addEventListener(
    'touchstart',
    function (event) {
      var carousel = closest(event.target, '[data-product-carousel]');
      if (!carousel || !event.touches.length) {
        touch = null;
        return;
      }
      var insideShelf = !!closest(carousel, '.grid-overflow-wrapper');
      touch = {
        carousel: carousel,
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        deltaX: 0,
        deltaY: 0,
        locked: null, // 'horizontal' | 'vertical' | null (not yet decided -- decided on the first touchmove)
        insideShelf: insideShelf, // see the big comment at the top of this file
      };
      if (insideShelf) {
        // Passive: never calls preventDefault, so this carousel's own
        // touch handling never competes with the shelf's native
        // horizontal scroll (or the page's native vertical scroll) for
        // this gesture. Still added/removed per-touch, same as the
        // non-passive path, purely so onCarouselTouchMove can update
        // touch.deltaX/deltaY for the tap check at touchend.
        document.addEventListener('touchmove', onCarouselTouchMove, { passive: true });
      } else {
        // Non-passive, and only for the duration of this one touch: this is
        // what lets onCarouselTouchMove's preventDefault() actually stop
        // page scroll once it decides a gesture is horizontal, without
        // adding that same wait-on-JS cost to every other touch on the page.
        document.addEventListener('touchmove', onCarouselTouchMove, { passive: false });
      }
    },
    { passive: true }
  );

  document.addEventListener('touchend', function () {
    if (!touch) return;

    var absX = Math.abs(touch.deltaX);
    var absY = Math.abs(touch.deltaY);

    if (touch.insideShelf) {
      // Swiping here is the shelf's own native horizontal scroll, not a
      // carousel advance -- see the big comment at the top of this file.
      // Only handle the plain-tap case (send to the product page, same
      // as the non-shelf path below); a real drag/swipe is left entirely
      // to the browser.
      if (absX <= TAP_TOLERANCE && absY <= TAP_TOLERANCE) {
        var shelfUrl = touch.carousel.getAttribute('data-product-url');
        if (shelfUrl) window.location.href = shelfUrl;
      }
      endCarouselTouch();
      return;
    }

    var horizontal = absX > absY;

    if (horizontal && absX > SWIPE_THRESHOLD) {
      // Deliberate horizontal swipe: advance the carousel. activate() now
      // clamps at either end instead of looping (see the big comment up
      // top), so swiping past the last image just stays on the last, and
      // swiping past the first stays on the first.
      var current = parseInt(touch.carousel.getAttribute('data-active-index') || '0', 10);
      var next = touch.deltaX < 0 ? current + 1 : current - 1;
      activate(touch.carousel, next);
    } else if (absX <= TAP_TOLERANCE && absY <= TAP_TOLERANCE) {
      // Barely moved: this is a tap, not a swipe or a page scroll. Send it
      // to the product page ourselves (see comment above).
      var url = touch.carousel.getAttribute('data-product-url');
      if (url) window.location.href = url;
    }
    // Otherwise this was a vertical drag that didn't clear either
    // threshold -- most likely the start of a page scroll. Leave it alone;
    // touch-action: pan-y on the track already lets the browser handle it.

    endCarouselTouch();
  });

  document.addEventListener('touchcancel', function () {
    endCarouselTouch();
  });
})();
